import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiMenu, FiX, FiPhone } from 'react-icons/fi';
import { cn } from '../utils/cn';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    if (!isHome) {
      e.preventDefault();
      navigate('/' + href);
    }
  };

  const navLinks = [
    { name: 'Übersicht', href: '#uebersicht' },
    { name: 'Hallenplan', href: '#hallenplan' },
    { name: 'Freiflächen', href: '#freiflaechen' },
    { name: 'Kontakt', href: '#kontakt' },
  ];

  return (
    <header className={cn(
      'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      isScrolled || !isHome ? 'bg-white shadow-md py-3' : 'bg-transparent py-5'
    )}>
      <div className="container px-4 mx-auto md:px-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex flex-col group">
            <span className={cn(
              "text-lg md:text-xl font-black uppercase tracking-tighter transition-colors duration-300",
              isScrolled || !isHome ? "text-emerald-950" : "text-white"
            )}>
              Handwerk- und Gewerbehof
            </span>
            <span className={cn(
              "text-xs md:text-sm font-bold uppercase tracking-widest transition-colors duration-300",
              isScrolled || !isHome ? "text-yellow-600" : "text-yellow-500"
            )}>
              Alte Schmiede
            </span>
          </Link>

          <nav className="items-center hidden gap-8 md:flex">
            <ul className="flex gap-8">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href} 
                    onClick={(e) => handleNavClick(e, link.href)}
                    className={cn(
                      "text-sm font-bold tracking-widest transition-colors hover:text-yellow-500 uppercase",
                      isScrolled || !isHome ? "text-emerald-950" : "text-white drop-shadow-sm"
                    )}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
            <a 
              href="#kontakt" 
              onClick={(e) => handleNavClick(e, '#kontakt')}
              className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-emerald-950 transition-all bg-yellow-500 rounded-sm hover:bg-yellow-400 shadow-md"
            >
              <SafeIcon icon={FiPhone} /> Anfragen
            </a>
          </nav>

          <button className="p-2 md:hidden" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className={cn("text-3xl", isScrolled || isMobileMenuOpen || !isHome ? "text-emerald-950" : "text-white")} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="absolute left-0 right-0 bg-white border-t border-gray-100 shadow-2xl top-full md:hidden"
          >
            <ul className="flex flex-col p-6">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href} 
                    onClick={(e) => {
                      handleNavClick(e, link.href);
                      setIsMobileMenuOpen(false);
                    }} 
                    className="block py-4 text-lg font-bold text-emerald-950 border-b border-gray-50"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
              <li className="pt-6">
                <a 
                  href="#kontakt" 
                  onClick={(e) => {
                    handleNavClick(e, '#kontakt');
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center justify-center w-full gap-2 px-5 py-4 font-bold text-center text-emerald-950 bg-yellow-500 rounded-sm"
                >
                  <SafeIcon icon={FiPhone} /> Jetzt anfragen
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}